import React, { Children, cloneElement, useState } from 'react';
import {
    arrayOf,
    element,
    number,
    object,
    oneOfType,
    string
} from 'prop-types';
import clsx from 'clsx';
import { Link, withRouter } from 'react-router-dom';

import Tabs from '@material-ui/core/Tabs';
import useFormId from '../../hooks/useFormId';

import TabNavItem from './TabNavItem';

import defaultClasses from './TabPanel.module.css';

/**
 * Returns TRUE if the tab node is selected.
 */
function isSelected(selectedValue, node, key) {
    return selectedValue === node.props.href || selectedValue === key;
}

/**
 * Returns the index of the selected tab
 */
function getSelectedTabValue(tabIds, selectedTabValue) {
    const idx = tabIds.findIndex((id, key) => id === selectedTabValue || key === selectedTabValue);
    return idx < 0 ? 0 : idx ;
}

/**
 * This Tab Panel provides a more succinct API over top of Material UI's Tab
 * API. It provides the ability to define a single block of Tab content
 * which is then extracted out into the seperate Material UI Tab links and Tab
 * Containers.
 */
const TabPanel = ({
    'aria-label': ariaLabel,
    children,
    classes,
    location,
    selectedTab,
    variant
}) => {
    const id = useFormId('tabpanel');
    const [selectedTabValue, setSelectedTabValue] = useState(selectedTab);

    // Extract each tab's navigational props to build the tab nav header and
    // build the content components.
    const tabContent = [];
    const tabIds = [];
    // Filter out null elements from tab to prevent tab to tabIndex mapping issue.
    // Only a problem while using the feature flag hiding the Score tab, can probably
    // remove this line of code and go back to using Children.map(children, (node, key) => {...})
    // once the FF is removed.
    const cleanChildren = Children.toArray(children).filter(Boolean);
    const tabs = cleanChildren.map((node, key) => {

        const tabId = node.props.href || key;
        const contentId = `${id}-${key}`;
        const tabComponent = node.props.href ? Link : undefined;
        const hidden = !isSelected(selectedTabValue, node, key);
        tabIds.push(tabId);
        tabContent.push(
            cloneElement(
                node,
                {
                    'aria-labelledby': `tab-${contentId}`,
                    key,
                    hidden,
                    id:                contentId,
                    role:              'tabpanel',
                    ...node.props
                },
                node.props.children
            )
        );
        return (
            <TabNavItem
                key={key}
                component={tabComponent}
                contentId={contentId}
                error={node.props.error}
                to={{
                    pathname: node.props.href,
                    search:   location.search
                }}
            >
                {node.props.title}
            </TabNavItem>
        );
    });

    function handleChange(event, newSelectedTab) {
        setSelectedTabValue(newSelectedTab);
    }

    return (
        <section className={clsx(defaultClasses.root, classes.root)}>
            <Tabs
                aria-label={ariaLabel}
                classes={{
                    indicator: defaultClasses.tabsIndicator,
                    root:      defaultClasses.tabsRoot
                }}
                onChange={handleChange}
                value={getSelectedTabValue(tabIds, selectedTabValue)}
                variant={variant}
            >
                {tabs}
            </Tabs>
            {tabContent}
        </section>
    );
};

TabPanel.defaultProps = {
    classes:     {},
    selectedTab: 0,
    variant:     'fullWidth'
};

TabPanel.propTypes = {
    'aria-label': string.isRequired,
    children:     oneOfType([element, arrayOf(element)]).isRequired,
    classes:      object,
    selectedTab:  oneOfType([number, string]),
    variant:      string
};

export default withRouter(TabPanel);
